export * from "ce/pages/AppViewer/NavigationLogo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import type { NavigationSetting } from "constants/AppConstants";
import { NAVIGATION_SETTINGS } from "constants/AppConstants";
import styled from "styled-components";
import classNames from "classnames";
import {
  getAppMode,
  getCurrentApplication,
} from "ee/selectors/applicationSelectors";
import type { ApplicationPayload } from "entities/Application";
import {
  getCurrentPageId,
  getViewModePageList,
} from "selectors/editorSelectors";
import { useHref } from "pages/Editor/utils";
import { APP_MODE } from "entities/App";
import { builderURL, viewerURL } from "ee/RouteBuilder";
import { get } from "lodash";
import { getAssetUrl } from "ee/utils/airgapHelpers";
import { getOrganizationConfig } from "ee/selectors/organizationSelectors";
import { getAppsmithConfigs } from "ee/configs";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import { FEATURE_FLAG } from "ee/entities/FeatureFlag";

interface NavigationLogoProps {
  logoConfiguration: NavigationSetting["logoConfiguration"];
}

const StyledImage = styled.img<{ maxWidth?: number; maxHeight?: number }>`
  max-width: ${(props) => (props.maxWidth ? `${props.maxWidth}px` : "10rem")};
  max-height: ${(props) =>
    props.maxHeight ? `${props.maxHeight}px` : "1.5rem"};
`;

export const { cloudHosting } = getAppsmithConfigs();

function NavigationLogo(props: NavigationLogoProps) {
  const { logoConfiguration } = props;
  const currentApplicationDetails: ApplicationPayload | undefined = useSelector(
    getCurrentApplication,
  );
  const pages = useSelector(getViewModePageList);
  const appMode = useSelector(getAppMode);
  const defaultPage = pages.find((page) => page.isDefault) || pages[0];
  const pageUrl = useHref(
    appMode === APP_MODE.PUBLISHED ? viewerURL : builderURL,
    {
      basePageId: defaultPage?.basePageId,
    },
  );
  const logoAssetId = get(
    currentApplicationDetails,
    "applicationDetail.navigationSetting.logoAssetId",
    "",
  );
  const organizationConfig = useSelector(getOrganizationConfig);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const currentPageId = useSelector(getCurrentPageId);

  const isLogoSizeEnabled = useFeatureFlag(
    FEATURE_FLAG.release_branding_logo_resize_enabled,
  );

  useEffect(() => {
    if (logoAssetId?.length) {
      setLogoUrl(`/api/v1/assets/${logoAssetId}`);

      return;
    } else if (cloudHosting) {
      setLogoUrl(null);

      return;
    } else if (!cloudHosting && organizationConfig?.brandLogoUrl) {
      setLogoUrl(organizationConfig.brandLogoUrl);

      return;
    }

    setLogoUrl(null);

    return;
  }, [logoAssetId, organizationConfig, cloudHosting]);

  if (
    !logoUrl ||
    logoConfiguration ===
      NAVIGATION_SETTINGS.LOGO_CONFIGURATION.APPLICATION_TITLE_ONLY ||
    logoConfiguration ===
      NAVIGATION_SETTINGS.LOGO_CONFIGURATION.NO_LOGO_OR_APPLICATION_TITLE
  ) {
    return null;
  }

  return (
    <Link
      className={classNames({
        "mr-4": true,
        "pointer-events-none select-none":
          pages.length <= 1 || defaultPage.pageId === currentPageId,
      })}
      to={pageUrl}
    >
      <StyledImage
        alt="Application's logo"
        maxHeight={
          isLogoSizeEnabled ? organizationConfig?.logoHeight : undefined
        }
        maxWidth={isLogoSizeEnabled ? organizationConfig?.logoWidth : undefined}
        src={getAssetUrl(logoUrl)}
      />
    </Link>
  );
}

export default NavigationLogo;
